<template>
	<DialogControl :attach="overlayTarget" v-model="open" fullscreen scrollable class="route-optimizer-dialog">
		<v-card class="route-optimizer-dialog__card" :class="{ 'route-optimizer-dialog__card--compact': !isDesktop }">
			<v-card-title class="route-optimizer-dialog__header">
				<v-icon color="primary">alt_route</v-icon>
				<span class="route-optimizer-dialog__title">{{ t('OptimizedRoute') }}</span>
				<v-chip size="small" color="primary" variant="tonal" class="route-optimizer-dialog__scope" readonly>{{ scopeLabel }}</v-chip>
				<v-spacer></v-spacer>
				<v-btn icon="close" variant="tonal" color="primary" density="comfortable" :aria-label="t('Close')" @click="open = false" />
			</v-card-title>

			<v-card-text class="route-optimizer-dialog__content">
				<div class="route-optimizer-dialog__toolbar">
					<v-btn prepend-icon="alt_route" variant="tonal" rounded="xl" color="primary" :block="!isDesktop" :loading="optimizer.loading.value" :disabled="!optimizer.canReoptimize.value" @click="optimizer.run()">{{ t('Reoptimize') }}</v-btn>
					<div class="route-optimizer-dialog__options">
						<label v-for="option in optionList" :key="option.key" class="route-optimizer-dialog__option" :class="{ 'route-optimizer-dialog__option--checked': optimizer.options[option.key] }">
							<v-checkbox-btn v-model="optimizer.options[option.key]" color="primary" density="compact" />
							<span class="route-optimizer-dialog__option-text">
								<strong>{{ t(option.label) }}</strong>
								<span>{{ t(option.hint) }}</span>
							</span>
						</label>
					</div>
				</div>

				<div v-if="alerts.length" class="route-optimizer-dialog__alerts">
					<v-alert v-for="alert in alerts" :key="alert.key" :type="alert.type" variant="tonal" density="compact">
						<strong v-if="alert.title">{{ alert.title }} ·</strong>
						{{ alert.text }}
					</v-alert>
				</div>
				<v-progress-linear v-if="optimizer.loading.value && !result" indeterminate color="primary" />

				<div v-if="result" class="route-optimizer-dialog__body" :class="{ 'route-optimizer-dialog__body--with-map': showMap }">
					<div class="route-optimizer-dialog__main">
						<div v-if="hasUpcomingDay" class="route-optimizer-dialog__summary" :class="{ 'route-optimizer-dialog__summary--loss': result.gain < 0 }">
							<v-icon class="route-optimizer-dialog__summary-icon">directions_car</v-icon>
							<div class="route-optimizer-dialog__summary-text">
								<span class="route-optimizer-dialog__summary-label">{{ isWeek ? t('WeekRouteTime') : t('RouteTime') }}</span>
								<span>
									<s>{{ formatDuration(result.travelBefore) }}</s>
									<strong class="ml-1">{{ formatDuration(result.travelAfter) }}</strong>
								</span>
							</div>
							<div class="route-optimizer-dialog__summary-gain">
								<strong>{{ formatGain(result.gain) }}</strong>
								<span>{{ summaryDetail }}</span>
							</div>
						</div>

						<div v-if="hasUpcomingDay && !optimizer.hasWork.value && !unplaceable.length" class="route-optimizer-dialog__optimal text-medium-emphasis">
							<v-icon size="18">check_circle</v-icon>
							{{ t('RouteAlreadyOptimal') }}
						</div>

						<ButtonToggleControl v-if="hasUpcomingDay" v-model="mode" class="route-optimizer-dialog__toggle" :items="modeItems" title-key-translate />

						<RouteOptimizerCalendar :days="result.days" :mode="mode" :type="isWeek ? 'week' : 'day'" :start="calendarStart" :weekdays="isWeek && weekdays.length ? weekdays : ALL_WEEKDAYS" />

						<section v-if="unplaceable.length" class="route-optimizer-dialog__unplaceable">
							<div class="route-optimizer-dialog__unplaceable-head">
								<h4 class="route-optimizer-dialog__section-title">{{ t('Unplaceable') }} · {{ unplaceable.length }}</h4>
								<span v-if="optimizer.undecided.value" class="text-caption text-warning">{{ t('UnplaceableChoiceRequired') }}</span>
							</div>
							<div class="route-optimizer-dialog__unplaceable-list">
								<div v-for="entry in unplaceable" :key="entry.item.uid" class="route-optimizer-dialog__unplaceable-item" :class="{ 'route-optimizer-dialog__unplaceable-item--removed': entry.decision === optimizer.DECISION.REMOVE }">
									<div class="route-optimizer-dialog__unplaceable-top">
										<v-chip size="x-small" color="warning" variant="tonal" prepend-icon="schedule">{{ t('Unplaceable') }}</v-chip>
										<v-icon v-for="choice in choiceList" :key="choice.decision" size="20" class="route-optimizer-dialog__choice" :class="choiceClass(entry, choice)" :title="t(choice.label)" :aria-label="t(choice.label)" :aria-pressed="entry.decision === choice.decision" @click="choose(entry, choice)" @keydown.enter.space.prevent="choose(entry, choice)">{{ choice.icon }}</v-icon>
									</div>
									<strong class="route-optimizer-dialog__unplaceable-label">{{ entry.item.label }}</strong>
									<span class="text-caption">{{ entry.dayLabel }}</span>
									<span class="text-caption text-medium-emphasis">{{ t(entry.keepEnd ? 'UnplaceableDayEnd' : 'UnplaceableWorkTime') }}</span>
									<span v-if="entry.decision" class="text-caption text-medium-emphasis">{{ t(entry.decision === optimizer.DECISION.REMOVE ? 'RemoveHint' : 'KeepHint') }}</span>
								</div>
							</div>
						</section>
					</div>

					<RouteOptimizerMap v-if="showMap" class="route-optimizer-dialog__map" :days="result.days" :points="optimizer.points.value" :mode="mode" />
				</div>
			</v-card-text>

			<v-card-actions class="route-optimizer-dialog__footer">
				<v-spacer></v-spacer>
				<v-btn text rounded="xl" :disabled="optimizer.applying.value" @click="open = false">{{ t('Cancel') }}</v-btn>
				<v-btn variant="tonal" rounded="xl" color="primary" prepend-icon="check" :loading="optimizer.applying.value" :disabled="!optimizer.canApply.value" @click="apply">{{ t('Apply') }}</v-btn>
			</v-card-actions>
		</v-card>
	</DialogControl>
</template>

<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue';
import DialogControl from '@/components/controls/DialogControl.vue';
import ButtonToggleControl from '@/components/controls/ButtonToggleControl.vue';
import RouteOptimizerCalendar from './RouteOptimizerCalendar.vue';
import RouteOptimizerMap from './RouteOptimizerMap.vue';
import { useTranslator } from '@/composables/useTranslator';
import { useRouteOptimizer } from '@/composables/useRouteOptimizer';
import { iframeDataBridge } from '@/iframe/iframeDataBridge';
import { deviceUtils } from '@/utils/core/deviceUtils';
import { formatDuration, formatGain } from '@/utils/domain/routeOptimizer';
import { toDayKey, readWorkingWeekdays, displayedDayKeys } from '@/utils/legacy/agendaRouteSource';

const props = defineProps({
	modelValue: { type: Boolean, default: false },
	overlayTarget: { type: Object, default: null },
	context: { type: Object, default: null },
});
const emit = defineEmits(['update:modelValue']);

const open = computed({
	get: () => props.modelValue,
	set: (value) => emit('update:modelValue', value),
});

const t = useTranslator();
const scope = computed(() => props.context);
const optimizer = useRouteOptimizer(scope);
const result = computed(() => optimizer.result.value);
const mode = ref('after');
const isDesktop = ref(!deviceUtils.isLegacyMobile());
const onResize = () => (isDesktop.value = !deviceUtils.isLegacyMobile());

const locale = iframeDataBridge.getCurrentUser()?.locale || 'fr-FR';
const isMapActivated = !!iframeDataBridge.getSalesHelper()?.isGMAPActivated?.();
const isWeek = computed(() => props.context?.viewType === 'timeGridWeek');
const startDate = computed(() => (props.context?.start ? toDayKey(props.context.start) : toDayKey(Date.now())));
const hasUpcomingDay = computed(() => !!result.value?.days.some((day) => !day.excluded));
const hasExcludedDay = computed(() => !!result.value?.days.some((day) => day.excluded));
const showMap = computed(() => isDesktop.value && isMapActivated && optimizer.isOnline.value && hasUpcomingDay.value);

const optionList = [
	{ key: 'keepStart', label: 'KeepDayStart', hint: 'KeepDayStartHint' },
	{ key: 'keepEnd', label: 'KeepDayEnd', hint: 'KeepDayEndHint' },
];
const modeItems = [
	{ value: 'before', title: 'Before' },
	{ value: 'after', title: 'After' },
];
const choiceList = [
	{ decision: optimizer.DECISION.KEEP, label: 'Keep', icon: 'check' },
	{ decision: optimizer.DECISION.REMOVE, label: 'Remove', icon: 'delete' },
];
const choose = (entry, choice) => optimizer.decide(entry.item.uid, choice.decision);
const choiceClass = (entry, choice) => [`route-optimizer-dialog__choice--${choice.decision}`, { 'route-optimizer-dialog__choice--active': entry.decision === choice.decision }];

const formatDay = (dateKey, options) => new Date(`${dateKey}T12:00:00`).toLocaleDateString(locale, options);

const isoWeek = (date) => {
	const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
	d.setUTCDate(d.getUTCDate() + 4 - (d.getUTCDay() || 7));
	return Math.ceil(((d - Date.UTC(d.getUTCFullYear(), 0, 1)) / 86400000 + 1) / 7);
};

const ALL_WEEKDAYS = [1, 2, 3, 4, 5, 6, 0];
const workingWeekdays = ref(null);
const dayKeys = computed(() => {
	if (!props.context?.start) return [];
	const busy = new Set((result.value?.days || []).filter((d) => d.steps.length || d.unplaceable.length).map((d) => d.date));
	return displayedDayKeys(props.context.start, props.context.end, workingWeekdays.value, busy);
});
const weekdays = computed(() => [...new Set(dayKeys.value.map((key) => new Date(`${key}T12:00:00`).getDay()))].sort((a, b) => ((a + 6) % 7) - ((b + 6) % 7)));
const calendarStart = computed(() => (isWeek.value && dayKeys.value.length ? dayKeys.value[0] : startDate.value));

const scopeLabel = computed(() => {
	if (!props.context?.start) return '';
	if (!isWeek.value) return `${t('Day')} · ${formatDay(startDate.value, { day: 'numeric', month: 'short' })}`;
	const first = dayKeys.value[0] ?? startDate.value;
	const last = dayKeys.value[dayKeys.value.length - 1] ?? startDate.value;
	return `${t('Week')} ${isoWeek(new Date(props.context.start))} · ${formatDay(first, { day: 'numeric', month: 'short' })} – ${formatDay(last, { day: 'numeric', month: 'short', year: 'numeric' })}`;
});

const summaryDetail = computed(() => {
	if (!result.value) return '';
	if (isWeek.value) return `${result.value.optimizedDays} ${t('DaysOptimized')}`;
	const day = result.value.days[0];
	const parts = [];
	if (day?.pinnedCount) parts.push(`${day.pinnedCount} ${t('Pinned')}`);
	parts.push(`${day?.reorderedCount ?? 0} ${t('VisitsReordered')}`);
	if (day?.unplaceable.length) parts.push(`${day.unplaceable.length} ${t('Unplaceable')}`);
	return parts.join(' · ');
});

const endExceeded = computed(() => !!result.value?.days.some((day) => day.endExceeded));
const alerts = computed(() => {
	const list = [];
	if (!optimizer.isOnline.value) list.push({ key: 'offline', type: 'info', text: t('OnlineMode needed') });
	if (optimizer.error.value) list.push({ key: 'error', type: 'error', text: t(optimizer.error.value === 'apply' ? 'RouteOptimizationApplyFailed' : 'RouteOptimizationFailed') });
	if (endExceeded.value) list.push({ key: 'dayEnd', type: 'warning', title: t('DayEndExceeded'), text: t('DayEndExceededHint') });
	if (hasExcludedDay.value) list.push({ key: 'pastDays', type: 'info', text: t('RoutePastDaysExcluded') });
	return list;
});
const unplaceable = computed(() => optimizer.unplaceable.value.map((entry) => ({ ...entry, decision: optimizer.decisions[entry.item.uid], dayLabel: formatDay(entry.date, { weekday: 'long', day: 'numeric', month: 'short' }) })));

watch([open, () => props.context], ([isOpen]) => {
	if (!isOpen) return;
	optimizer.reset();
	mode.value = 'after';
	workingWeekdays.value = readWorkingWeekdays();
	optimizer.run();
});

async function apply() {
	if (await optimizer.apply()) open.value = false;
}

onMounted(() => window.addEventListener('resize', onResize));
onBeforeUnmount(() => window.removeEventListener('resize', onResize));
</script>
